import React from 'react'
import EmailCapture from '../components/EmailCapture'

const PricingPage: React.FC = () => {
  const plans = [
    {
      id: 'free',
      name: 'Free',
      price: 0,
      interval: 'forever',
      description: 'Perfect for getting started with market sentiment',
      features: [
        'Live wave chart visualization',
        'Top 1 stock mover daily',
        'Weekly sentiment summaries',
        'Basic market indicators',
        'Mobile-responsive dashboard',
      ],
      limitations: [
        'Limited to top 1 stock',
        'No historical data export',
        'Standard support only',
      ],
      buttonText: 'Current Plan',
      popular: false,
    },
    {
      id: 'premium',
      name: 'Premium',
      price: 9.99,
      interval: 'month',
      description: 'Unlock the full power of Stocksines',
      features: [
        'Everything in Free',
        'Complete ranked stock list',
        'All stocks by signal strength',
        'Advanced filtering options',
        'Historical data export',
        'Priority customer support',
      ],
      limitations: [],
      buttonText: 'Coming Soon',
      popular: true,
    },
  ]

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16 text-center">
          <h1 className="text-4xl font-bold text-gray-900 mb-4">
            Simple, Transparent Pricing
          </h1>
          <p className="text-xl text-gray-600 max-w-2xl mx-auto">
            Choose the plan that fits your trading needs. Premium access coming soon!
          </p>
        </div>
      </div>

      {/* Pricing Cards */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 max-w-4xl mx-auto">
          {plans.map((plan) => (
            <div
              key={plan.id}
              className={`
                relative rounded-2xl shadow-lg overflow-hidden
                ${plan.popular 
                  ? 'bg-gradient-to-b from-primary-50 to-white border-2 border-primary-200' 
                  : 'bg-white border border-gray-200'
                }
              `}
            >
              {plan.popular && (
                <div className="absolute top-0 left-0 right-0 bg-gradient-to-r from-primary-600 to-primary-700 text-white text-center py-2 text-sm font-medium">
                  Most Popular
                </div>
              )}

              <div className={`p-8 ${plan.popular ? 'pt-12' : ''}`}>
                {/* Plan Header */}
                <div className="text-center mb-8">
                  <h3 className="text-2xl font-bold text-gray-900 mb-2">
                    {plan.name}
                  </h3>
                  <p className="text-gray-600 mb-4">
                    {plan.description}
                  </p>
                  <div className="flex items-baseline justify-center">
                    <span className="text-4xl font-bold text-gray-900">
                      ${plan.price}
                    </span>
                    {plan.price > 0 && (
                      <span className="text-gray-600 ml-1">
                        /{plan.interval}
                      </span>
                    )}
                  </div>
                </div>

                {/* Features */}
                <div className="mb-8">
                  <h4 className="font-semibold text-gray-900 mb-4">
                    What's included:
                  </h4>
                  <ul className="space-y-3">
                    {plan.features.map((feature, index) => (
                      <li key={index} className="flex items-start space-x-3">
                        <svg className="w-5 h-5 text-bullish-500 mt-0.5 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                          <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                        </svg>
                        <span className="text-gray-700">{feature}</span>
                      </li>
                    ))}
                  </ul>

                  {plan.limitations.length > 0 && (
                    <div className="mt-6">
                      <h4 className="font-semibold text-gray-900 mb-3">
                        Limitations:
                      </h4>
                      <ul className="space-y-2">
                        {plan.limitations.map((limitation, index) => (
                          <li key={index} className="flex items-start space-x-3">
                            <svg className="w-5 h-5 text-gray-400 mt-0.5 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                              <path fillRule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clipRule="evenodd" />
                            </svg>
                            <span className="text-gray-600">{limitation}</span>
                          </li>
                        ))}
                      </ul>
                    </div>
                  )}
                </div>

                {/* CTA Button */}
                {plan.id === 'free' ? (
                  <button
                    disabled
                    className="w-full py-3 px-6 rounded-lg font-medium transition-all duration-200 bg-gray-300 text-gray-500 cursor-not-allowed"
                  >
                    Currently Available
                  </button>
                ) : (
                  <div>
                    <div className="bg-yellow-100 border border-yellow-300 rounded-lg p-4">
                      <div className="flex items-center justify-center mb-2">
                        <svg className="w-5 h-5 text-yellow-600 mr-2" fill="currentColor" viewBox="0 0 20 20">
                          <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm1-12a1 1 0 10-2 0v4a1 1 0 00.293.707l2.828 2.829a1 1 0 101.415-1.415L11 9.586V6z" clipRule="evenodd" />
                        </svg>
                        <span className="text-yellow-800 font-semibold">Launching Soon!</span>
                      </div>
                      <p className="text-sm text-yellow-700 text-center mb-3">
                        Be the first to know when Premium launches
                      </p>
                      <EmailCapture source="pricing-premium-card" />
                    </div>
                  </div>
                )}
              </div>
            </div>
          ))}
        </div>

        {/* FAQ Section */}
        <div className="max-w-3xl mx-auto mt-20">
          <h2 className="text-3xl font-bold text-gray-900 text-center mb-12">
            Frequently Asked Questions
          </h2>
          
          <div className="space-y-8">
            <div>
              <h3 className="text-lg font-semibold text-gray-900 mb-2">
                When is the data updated?
              </h3>
              <p className="text-gray-600">
                StockSines updates all market data daily after market close (4:00 PM ET). 
                You'll get fresh sentiment analysis and stock rankings every trading day.
              </p>
            </div>

            <div>
              <h3 className="text-lg font-semibold text-gray-900 mb-2">
                When will Premium launch?
              </h3>
              <p className="text-gray-600">
                We're working hard to bring you Premium access! Sign up for our waitlist to be 
                notified as soon as it's available. Early subscribers will receive special launch pricing.
              </p>
            </div>

            <div>
              <h3 className="text-lg font-semibold text-gray-900 mb-2">
                What payment methods will you accept?
              </h3>
              <p className="text-gray-600">
                We'll accept all major credit cards (Visa, MasterCard, American Express) 
                and debit cards through our secure Stripe payment processing.
              </p>
            </div>

            <div>
              <h3 className="text-lg font-semibold text-gray-900 mb-2">
                How is market sentiment calculated?
              </h3>
              <p className="text-gray-600">
                Our proprietary algorithm analyzes multiple market indicators including options flow, 
                volume patterns, and price movements to generate sentiment scores. The exact methodology 
                is our competitive advantage and remains confidential.
              </p>
            </div>

            <div>
              <h3 className="text-lg font-semibold text-gray-900 mb-2">
                Will there be a free trial?
              </h3>
              <p className="text-gray-600">
                We're considering offering a 7-day free trial for Premium when it launches. 
                Join our waitlist to get early access and special offers!
              </p>
            </div>
          </div>
        </div>

        {/* CTA Section */}
        <div className="text-center mt-20">
          <h2 className="text-2xl font-bold text-gray-900 mb-4">
            Ready to ride the waves?
          </h2>
          <p className="text-gray-600 mb-8">
            Join thousands of traders waiting for Premium access
          </p>
          <div className="max-w-md mx-auto">
            <EmailCapture source="pricing-bottom-cta" />
          </div>
        </div>
      </div>
    </div>
  )
}

export default PricingPage